import { useCallback } from 'react';
import { AssetsService } from '@inference.engine/assets-api-client/web';
import { queryOptions, useQuery } from '@tanstack/react-query';

import { assetUrlsQueryOptions } from '@/entities/asset';
import { assetsHttp } from '@/lib/api/assets-client';
import { isAssetId } from '@/shared/lib/asset-id';

export interface ResolvedAttachment {
  name: string;
  mimeType: string;
  /** Signed and short-lived, so absent whenever the url query has not answered yet. */
  url?: string;
}

interface AttachmentMetadata {
  name: string;
  mimeType: string;
}

type AttachmentMetadataMap = Readonly<Record<string, AttachmentMetadata>>;

/**
 * Sorted and de-duplicated so two sections holding the same attachments in a different order
 * share one cache entry, and filtered because the service refuses a whole batch over one
 * malformed id. A filtered-out id is simply unresolved, which the chip already renders.
 */
const cacheableIds = (assetIds: readonly string[]): string[] =>
  [...new Set(assetIds.filter(isAssetId))].sort();

/**
 * Filename and mime type for a set of asset ids.
 *
 * Fetched per id because the service exposes no batch metadata endpoint, and settled rather
 * than awaited together so one deleted attachment cannot take the names of the others with it.
 *
 * Never stale: an asset id is immutable, and so are the name and type behind it. This is the
 * opposite of the url query beside it, which expires on purpose.
 */
const attachmentMetadataQueryOptions = (assetIds: readonly string[]) => {
  const ids = cacheableIds(assetIds);

  return queryOptions({
    queryKey: ['brief-section-attachments', ids],
    enabled: ids.length > 0,
    staleTime: Infinity,
    queryFn: async ({ signal }): Promise<AttachmentMetadataMap> => {
      const settled = await Promise.allSettled(
        ids.map((assetId) =>
          AssetsService.getAsset({ client: assetsHttp, path: { assetId }, signal }),
        ),
      );

      return Object.fromEntries(
        settled
          .flatMap((result) => (result.status === 'fulfilled' ? [result.value.data] : []))
          .flatMap((asset) =>
            asset ? [[asset.id, { name: asset.originalFilename, mimeType: asset.mimeType }]] : [],
          ),
      );
    },
  });
};

/**
 * Turns the ids a section stores into something a person can recognise.
 *
 * Two queries rather than one because they expire on opposite schedules, and because the url
 * query is shared with every other surface in the app rendering the same asset.
 */
export function useAttachmentResolver(
  assetIds: readonly string[],
): (assetId: string) => ResolvedAttachment | undefined {
  const metadata = useQuery(attachmentMetadataQueryOptions(assetIds));
  const urls = useQuery(assetUrlsQueryOptions(assetIds));

  const metadataByAssetId = metadata.data;
  const urlByAssetId = urls.data;

  return useCallback(
    (assetId: string) => {
      const asset = metadataByAssetId?.[assetId];
      if (!asset) return undefined;
      return { ...asset, url: urlByAssetId?.[assetId] };
    },
    [metadataByAssetId, urlByAssetId],
  );
}
